/**
 * Firebase Analytics (GA4) com gate de consentimento.
 *
 * Só inicializa quando:
 * - browser
 * - NODE_ENV === "production"
 * - emuladores desligados
 * - Measurement ID presente
 * - consentimento explícito de Analytics
 *
 * Sem fila retroativa. Sem setUserId / propriedades pessoais.
 */

import {
  getAnalytics,
  initializeAnalytics,
  isSupported,
  logEvent,
  setAnalyticsCollectionEnabled,
} from "firebase/analytics";
import app from "@/config/firebase";
import {
  hasAnalyticsConsent,
  subscribePrivacyConsent,
} from "@/services/privacy/privacyConsentService";
import { sanitizePagePath } from "@/services/analytics/pagePath";

/** @type {import("firebase/analytics").Analytics | null} */
let analyticsInstance = null;

/** @type {Promise<import("firebase/analytics").Analytics | null> | null} */
let initPromise = null;

/** @type {boolean} */
let collectionEnabled = false;

/** @type {Record<string, string | undefined> | null} */
let envOverride = null;

/** @type {null | {
 *   isSupported: typeof isSupported,
 *   initializeAnalytics: typeof initializeAnalytics,
 *   getAnalytics: typeof getAnalytics,
 *   logEvent: typeof logEvent,
 *   setAnalyticsCollectionEnabled: typeof setAnalyticsCollectionEnabled,
 * }} */
let analyticsApiOverride = null;

function getAnalyticsApi() {
  return (
    analyticsApiOverride || {
      isSupported,
      initializeAnalytics,
      getAnalytics,
      logEvent,
      setAnalyticsCollectionEnabled,
    }
  );
}

const SENSITIVE_PARAM_KEYS = new Set([
  "email",
  "password",
  "name",
  "display_name",
  "phone",
  "phone_number",
  "token",
  "access_token",
  "refresh_token",
  "api_key",
  "secret",
  "credit_card",
  "card_number",
  "cvv",
  "cpf",
  "cnpj",
  "address",
  "ip_address",
  "uid",
  "user_id",
  "userid",
  "project_id",
  "projectid",
  "image_id",
  "imageid",
  "slug",
  "url",
  "href",
  "page_location",
  "page_referrer",
  "search",
  "hash",
]);

function getEnv() {
  return envOverride || process.env;
}

function isBrowser() {
  return typeof window !== "undefined";
}

/**
 * @param {Record<string, string | undefined>} [env]
 * @param {boolean} [consentGranted]
 * @returns {{
 *   isBrowser: boolean,
 *   isProduction: boolean,
 *   useEmulator: boolean,
 *   hasMeasurementId: boolean,
 *   hasConsent: boolean,
 * }}
 */
export function getAnalyticsRuntimeFlags(env = getEnv(), consentGranted = hasAnalyticsConsent()) {
  return {
    isBrowser: isBrowser(),
    isProduction: env.NODE_ENV === "production",
    useEmulator: env.REACT_APP_USE_FIREBASE_EMULATORS === "true",
    hasMeasurementId: Boolean(env.REACT_APP_FIREBASE_MEASUREMENT_ID?.trim()),
    hasConsent: consentGranted === true,
  };
}

/**
 * @param {{ env?: Record<string, string | undefined>, consentGranted?: boolean }} [options]
 * @returns {boolean}
 */
export function canInitializeAnalytics(options = {}) {
  const flags = getAnalyticsRuntimeFlags(
    options.env || getEnv(),
    options.consentGranted ?? hasAnalyticsConsent(),
  );

  return (
    flags.isBrowser &&
    flags.isProduction &&
    !flags.useEmulator &&
    flags.hasMeasurementId &&
    flags.hasConsent
  );
}

export function isAnalyticsEnabled() {
  return canInitializeAnalytics();
}

/**
 * @param {string} value
 * @returns {string}
 */
export function normalizeAnalyticsKey(value) {
  if (typeof value !== "string" || !value.trim()) {
    return "";
  }

  return value
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s-]+/g, "_")
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "");
}

/**
 * @param {Record<string, unknown> | undefined | null} params
 * @returns {Record<string, string | number | boolean>}
 */
function sanitizeParams(params) {
  if (!params || typeof params !== "object") {
    return {};
  }

  /** @type {Record<string, string | number | boolean>} */
  const sanitized = {};

  for (const [rawKey, rawValue] of Object.entries(params)) {
    const key = normalizeAnalyticsKey(rawKey);

    if (!key || SENSITIVE_PARAM_KEYS.has(key)) {
      continue;
    }

    if (rawValue == null) {
      continue;
    }

    const valueType = typeof rawValue;

    if (valueType === "string") {
      const value = rawValue.trim();
      if (!value || value.includes("@")) {
        continue;
      }
      sanitized[key] = value.slice(0, 100);
      continue;
    }

    if (valueType === "number" || valueType === "boolean") {
      sanitized[key] = rawValue;
    }
  }

  return sanitized;
}

/**
 * @param {string} visibility
 * @returns {"public" | "private" | "link"}
 */
export function toShareVisibilityParam(visibility) {
  if (visibility === "shared") {
    return "link";
  }

  if (visibility === "public") {
    return "public";
  }

  return "private";
}

async function resolveAnalyticsInstance() {
  if (!canInitializeAnalytics()) {
    return null;
  }

  if (analyticsInstance) {
    return analyticsInstance;
  }

  if (initPromise) {
    return initPromise;
  }

  initPromise = (async () => {
    const api = getAnalyticsApi();

    try {
      const supported = await api.isSupported();
      if (!supported || !canInitializeAnalytics()) {
        return null;
      }

      try {
        analyticsInstance = api.initializeAnalytics(app, {
          config: {
            send_page_view: false,
            allow_google_signals: false,
            allow_ad_personalization_signals: false,
          },
        });
      } catch {
        analyticsInstance = api.getAnalytics(app);
      }

      collectionEnabled = true;
      return analyticsInstance;
    } catch {
      return null;
    } finally {
      initPromise = null;
    }
  })();

  return initPromise;
}

/**
 * @param {boolean} enabled
 */
export async function applyAnalyticsConsent(enabled) {
  const api = getAnalyticsApi();

  if (enabled) {
    const analytics = await resolveAnalyticsInstance();
    if (analytics) {
      api.setAnalyticsCollectionEnabled(analytics, true);
      collectionEnabled = true;
    }
    return analytics;
  }

  if (analyticsInstance) {
    api.setAnalyticsCollectionEnabled(analyticsInstance, false);
    collectionEnabled = false;
  }

  return null;
}

subscribePrivacyConsent((state) => {
  void applyAnalyticsConsent(state?.analytics === true);
});

/**
 * @param {string} name
 * @param {Record<string, unknown>} [params]
 */
export function trackEvent(name, params = {}) {
  const eventName = normalizeAnalyticsKey(name);

  if (!eventName || !canInitializeAnalytics()) {
    return;
  }

  const payload = sanitizeParams(params);

  void resolveAnalyticsInstance()
    .then((analytics) => {
      if (!analytics || !collectionEnabled || !canInitializeAnalytics()) {
        return;
      }
      getAnalyticsApi().logEvent(analytics, eventName, payload);
    })
    .catch(() => {
      // Falha silenciosa — analytics nunca deve quebrar o app.
    });
}

/**
 * @param {string} path
 * @param {string} [title]
 */
export function trackPageView(path, title) {
  if (!canInitializeAnalytics()) {
    return;
  }

  const pagePath = sanitizePagePath(path);
  const pageTitle =
    typeof title === "string" && title.trim()
      ? title.trim().slice(0, 100)
      : isBrowser()
        ? document.title.slice(0, 100)
        : "";

  trackEvent("page_view", {
    page_path: pagePath,
    page_title: pageTitle,
  });
}

/**
 * Identificação individual removida. Mantido como no-op para não quebrar AuthContext.
 *
 * @param {{ uid?: string, planId?: string } | null | undefined} [_user]
 */
export function setAnalyticsUser(_user) {
  // Intencionalmente vazio: não enviamos UID, e-mail, nome ou plan_id pessoal.
}

export function getAnalyticsDebugState() {
  return {
    initialized: analyticsInstance != null,
    collectionEnabled,
    canInitialize: canInitializeAnalytics(),
  };
}

export function __setAnalyticsEnvForTests(env) {
  envOverride = env;
}

export function __setAnalyticsApiForTests(api) {
  analyticsApiOverride = api;
}

export function __resetAnalyticsForTests() {
  analyticsInstance = null;
  initPromise = null;
  collectionEnabled = false;
  envOverride = null;
  analyticsApiOverride = null;
}

export const analyticsConfig = {
  isEnabled: isAnalyticsEnabled,
  canInitialize: canInitializeAnalytics,
  sanitizePagePath,
};
